/**
 * Blog post data — pure content, no imports needed here beyond React inside pages.
 * Each post is targeted at a real, high-intent Google query.
 *
 * SEO principles applied:
 *  - Long-form (900–1,300 words)
 *  - Question-based H2 headings (match "People Also Ask")
 *  - Internal links to /, /register, and cross-linked between posts
 *  - Unique meta description + JSON-LD Article schema per post
 */

export const BLOG_POSTS = [
  {
    slug: "how-much-should-couples-save-each-month",
    title: "How much should couples save each month? (a straight answer with real numbers)",
    excerpt:
      "Most articles say '20% of income' and stop there. Here's a real, adjustable framework based on your household income, life stage, and shared goals — with US-specific benchmarks.",
    metaDescription:
      "How much should couples actually save each month? A practical, income-based framework with real US benchmarks — for both partners, at every life stage.",
    readingMinutes: 7,
    date: "2026-02-11",
    author: "The Tracker Daily team",
    tags: ["couples finance", "savings", "money and relationships"],
    heroLine:
      "Savings targets aren't universal — they're personal. Here's how to find yours together.",
  },
  {
    slug: "50-30-20-rule-vs-cash-flow-tracking",
    title: "The 50/30/20 rule vs cash-flow tracking: which one actually works?",
    excerpt:
      "The 50/30/20 rule is famous. Cash-flow tracking is unfamous. Here's an honest side-by-side of what they each really do — and which one Americans actually stick with past month one.",
    metaDescription:
      "Compare the 50/30/20 budget rule with modern cash-flow tracking. Learn which actually works, when to use each, and how to combine them for sustainable money habits.",
    readingMinutes: 6,
    date: "2026-02-11",
    author: "The Tracker Daily team",
    tags: ["personal finance", "budgeting", "cash flow"],
    heroLine:
      "The best budget system isn't the most elegant one — it's the one you'll still be using in six months.",
  },
  {
    slug: "how-to-categorize-your-expenses",
    title: "How to categorize your expenses (without losing your mind)",
    excerpt:
      "The perfect expense-tracking system has 8-12 categories, not 50. Here's the exact category list we recommend, why 'Groceries' and 'Dining' should always be separate, and when a category is worth its own line.",
    metaDescription:
      "The complete guide to categorizing your expenses. Learn the exact 12 categories that cover 95% of real US spending, plus what to do with edge cases.",
    readingMinutes: 6,
    date: "2026-02-11",
    author: "The Tracker Daily team",
    tags: ["personal finance", "budgeting basics", "categories"],
    heroLine:
      "Too few categories and you learn nothing. Too many and you'll never fill them in. There's a sweet spot.",
  },
  {
    slug: "how-to-track-money-coming-in-and-going-out",
    title: "How to track every dollar coming in and going out (without a spreadsheet)",
    excerpt:
      "A simple 3-step system for people who want honest visibility into their money — without connecting a bank account or wrangling a spreadsheet.",
    metaDescription:
      "Learn a simple 3-step system to track every dollar in and out of your account. No bank connection, no spreadsheets. Free personal finance tracker for the US.",
    readingMinutes: 6,
    date: "2026-02-04",
    author: "The Tracker Daily team",
    tags: ["personal finance", "cash flow", "budgeting basics"],
    heroLine: "You can't fix what you don't measure — and money is no exception.",
  },
  {
    slug: "how-to-budget-as-a-couple",
    title: "How to budget as a couple without arguments (a fair 5-step framework)",
    excerpt:
      "Money is the #1 thing couples fight about. Here's the fair, no-blame framework we've seen work for hundreds of couples — plus the exact tools to make it stick.",
    metaDescription:
      "A fair, argument-free 5-step framework for budgeting as a couple. Learn how to combine finances, split shared expenses, and build a joint budget you both trust.",
    readingMinutes: 8,
    date: "2026-02-04",
    author: "The Tracker Daily team",
    tags: ["couples finance", "shared budget", "money and relationships"],
    heroLine:
      "Money isn't just math for couples — it's power, safety, and shared vision. A good system respects all three.",
  },
];

export function getPostBySlug(slug) {
  return BLOG_POSTS.find((p) => p.slug === slug);
}

export function getRelatedPosts(currentSlug, limit = 2) {
  return BLOG_POSTS.filter((p) => p.slug !== currentSlug).slice(0, limit);
}
